import classNames from "classnames";
import React from "react";
import { t } from "ttag";

import * as userAPI from "../../../api/user";
import { Form } from "../../../forms/Form";
import { FormInput } from "../../../forms/FormInput";
import { FormSubmit } from "../../../forms/FormSubmit";
import { NonFieldErrors } from "../../../forms/NonFieldErrors";
import { strings } from "../../../localization";
import { IUser } from "../../../models/user";
import { updateGTMUserHash } from "../../../utils/analytics";
import { loadCurrentUser } from "../../common/thunks/user";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { Step } from "../constants";
import { CheckoutStep } from "./CheckoutStepContainer";
import { EmailAddressSummary } from "./EmailAddressSummary";

interface IProps {
    user: IUser;
    onContinue: (event?: React.MouseEvent<HTMLElement>) => void;
    onEdit: (event?: React.MouseEvent<HTMLElement>) => void;
}

const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
    email: string,
    emailErrors: string[] | undefined,
    dispatch: ReturnType<typeof useAppDispatch>,
    setIsSaving: (value: boolean) => void,
    setErrors: (errors: {
        non_field_errors?: string[];
        email?: string[];
    }) => void,
    onContinue: (event?: React.MouseEvent<HTMLElement>) => void,
): Promise<void> => {
    event.preventDefault();
    if (emailErrors && emailErrors.length > 0) {
        return;
    }
    setIsSaving(true);
    setErrors({});
    await updateGTMUserHash(email);
    try {
        await userAPI.updateEmailAddress(email);
        await dispatch(loadCurrentUser());
        setIsSaving(false);
        setErrors({});
        onContinue();
    } catch (error: unknown) {
        setIsSaving(false);
        if (
            error &&
            typeof error === "object" &&
            "response" in error &&
            error.response &&
            typeof error.response === "object" &&
            "body" in error.response
        ) {
            setErrors(
                error.response.body as {
                    non_field_errors?: string[];
                    email?: string[];
                },
            );
        }
    }
};

const handleValidStateChange = (
    _: string,
    errorMessages: string[],
    errors: { non_field_errors?: string[]; email?: string[] },
    setErrors: (errors: {
        non_field_errors?: string[];
        email?: string[];
    }) => void,
): void => {
    const newErrors = { ...errors, email: errorMessages };
    setErrors(newErrors);
};

export const EmailAddress = (props: IProps): React.ReactNode => {
    const showForm = useAppSelector(
        (state) => state.checkout.form.current_step === Step.LOGIN,
    );
    const dispatch = useAppDispatch();

    const [isSaving, setIsSaving] = React.useState(false);
    const [email, setEmail] = React.useState("");
    const [errors, setErrors] = React.useState<{
        non_field_errors?: string[];
        email?: string[];
    }>({});

    const emailInputRef = React.useRef<HTMLInputElement>(null);
    const previousShowFormRef = React.useRef(showForm);

    React.useEffect(() => {
        if (previousShowFormRef.current === showForm) {
            return;
        }

        previousShowFormRef.current = showForm;
        setEmail(props.user.email);
    }, [showForm, props.user.email]);

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        await handleSubmit(
            event,
            email,
            errors.email,
            dispatch,
            setIsSaving,
            setErrors,
            props.onContinue,
        );
    };

    const onValidStateChange = (_: string, errorMessages: string[]) => {
        handleValidStateChange(_, errorMessages, errors, setErrors);
    };

    const onEmailChange = () => {
        setEmail(emailInputRef.current?.value || "");
    };

    const bodyClasses = classNames({
        "checkout-step__body": true,
        "checkout-step__body--collapsed": !showForm,
    });

    return (
        <CheckoutStep
            className="checkout-step--email checkout-step--first"
            heading={t`Email Address`}
        >
            <div className={bodyClasses}>
                <Form onSubmit={onSubmit}>
                    <NonFieldErrors
                        errors={errors.non_field_errors}
                        showErrorMessages={true}
                    />
                    <FormInput
                        inputRef={emailInputRef}
                        label={t`Email Address`}
                        labelPlacement="dynamic"
                        name="email"
                        id="email"
                        validation={["required", "email"]}
                        value={email}
                        onChange={onEmailChange}
                        errors={errors.email}
                        disabled={isSaving}
                        onValidStateChange={onValidStateChange}
                        showErrorMessages={true}
                    />
                    <FormSubmit
                        value={
                            // TODO(i18n): Use t tagged template for fallback: strings.get("CONTINUE_BTN_LABEL_LOGIN") || t`CONTINUE`
                            strings.get("CONTINUE_BTN_LABEL_LOGIN") ||
                            "CONTINUE"
                        }
                        disabled={isSaving}
                    />
                </Form>
                <p>
                    <a
                        className="checkout-step__cancel"
                        href="#"
                        onClick={props.onContinue}
                    >
                        {t`Cancel`}
                    </a>
                </p>
            </div>
            <EmailAddressSummary user={props.user} onEdit={props.onEdit} />
        </CheckoutStep>
    );
};
